import { ShiftCodeKey } from './shiftCodes';
import { NotificationSubscription } from './notifications';
import { Permission } from "./permissions";
import { DateTime } from "luxon";

/* Shift Interfaces */
// Minimal User interface for limited user information access
export interface MinimalUser {
  id: string;                 // Firebase Auth UID
  full_name: string;           // Full name of the user
}

// User Table
export interface User {
  id: string;                         // Firebase Auth UID
  full_name: string;                   // Full name of the user
  email: string;                      // User's email address
  fansmetric_email?: string;           // User's fansmetric email address
  tax?: {                            // User's tax information
    country: string;                  // Country of tax residence
    confirmed_at: DateTime;            // When the tax country was confirmed
  };
  date_of_birth?: DateTime;             // User's DateTime of birth
  roles: string[];                    // User's roles - array of role IDs
  pay_class: 'UK' | 'FP' | undefined;  // User's pay class
  warnings?: Warning[];               // User warnings
  created_at: DateTime;                // Timestamp of creation
  updated_at: DateTime;                // Timestamp of last update
  role_permissions?: Permission[];     // Map of role IDs to permissions
  deactivated_at?: DateTime;           // Timestamp of deactivation
  deactivation_completed?: boolean;    // Whether the user is deactivated
  terms_accepted?: {                   // Terms acceptance information
    accepted_at: DateTime;             // When the terms were accepted
    version: string;                  // Version of terms accepted
  };
  privacy_policy_accepted?: {           // Privacy policy acceptance information
    accepted_at: DateTime;             // When the privacy policy was accepted
    version: string;                  // Version of privacy policy accepted
  };
  verified: boolean;                  // Whether the user has been verified
  verified_at: DateTime;               // When the user was verified
  verified_by: string;                // ID of the user who verified the user
  start_date: DateTime;                // When the user started working for the company
  notification_subscriptions: NotificationSubscription[];
  discord?: {                        // Discord account information (user-facing data only)
    id: string;                      // Discord user ID
    username: string;                // Discord username
    avatar: string | null;           // Discord avatar hash
    linked_at: DateTime;              // When the Discord account was linked
  };
}

// UserDiscord Table - separate table for Discord OAuth tokens
export interface UserDiscord {
  user_id: string;                   // Foreign key to users table
  discord_id: string;                // Discord user ID
  access_token: string;              // Discord OAuth access token
  refresh_token: string;             // Discord OAuth refresh token
  expires_at: DateTime;               // When the access token expires
}

// Warning interface for user warnings
export interface Warning {
  id: string;                 // Unique ID for the warning
  message: string;            // Warning message
  issued_by: string;          // ID of the user who issued the warning
  issued_at: DateTime;       // When the warning was issued
}

// Model Table
export interface Model {
  id: string;                 // Unique ID for the model
  name: string;               // Name of the model
  set: number;                // Set number for the model
  pay_class: 'UK' | 'FP' | undefined;  // Pay class for the model
  account_type: 'Normal' | 'Unlock' | 'Test';  // Account type for the model
  groups: string[];           // Groups for filtering (e.g., 'Shifts', 'Schedule')
  spam_post_group?: string | null;     // Spam post group for the model, e.g. 'A1-5', 'B1-5'
  fan_gain_group?: string | null;      // Fan gain group for the model, e.g. 'A', 'B', 'C', 'D', '1', '2', '3'
  weekend_group: number;       // Weekend group for the model, e.g. 1, 2, 3
  scheduling_group?: string;   // Scheduling group for the model (alphanumeric characters)
  scheduling_sub_groups?: ('TOP' | 'SUB_100')[];  // Scheduling sub-groups for the model
  paused?: boolean;           // Whether the model is paused (no shifts will be generated)
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// Shift Table
export interface Shift {
  id: string;                 // Unique ID for the shift
  fixed_shift_id?: string;      // Reference to the FixedShift it originated from (nullable)
  model_id: string;            // Reference to the Model
  user_id?: string | null;     // User assigned to this specific shift (nullable)
  trainer_id?: string | null;  // Trainer assigned to this specific shift (nullable)
  start_date_time: DateTime;     // Start datetime
  end_date_time: DateTime;       // End datetime
  ppv_earned: number;          // Currency earned for the shift (PPV)
  noppv_earned: number;        // Currency earned for the shift (Non-PPV)
  livestream_earned: number;   // Currency earned for the shift (Livestream)
  is_livestream_shift: boolean; // Whether this is a livestream shift
  shift_code: ShiftCodeKey;    // The shift code (e.g. "6-2 S", "2-10 FP", etc)
  submitted: boolean;         // Is the shift complete?
  clocked_in: boolean;         // Has the user clocked in?
  clock_in_time?: DateTime;      // When did the user clock in?
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
  verification_required: boolean; // Whether the shift requires verification
  verified_by?: string;        // The user who verified the shift
  verified_at?: DateTime;       // The DateTime and time the shift was verified
}

// SpamShift Table
export interface SpamShift {
  id: string;                 // Unique ID for the shift
  group: string;              // A1-5 | B1-5
  user_id?: string | null;     // User assigned to this specific shift (nullable)
  start_date_time: DateTime;     // Start datetime
  end_date_time: DateTime;       // End datetime
  submitted: boolean;         // Is the shift complete?
  clocked_in: boolean;         // Has the user clocked in?
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// SpamFixedShift Table
export interface SpamFixedShift {
  id: string;                 // Unique ID for the recurring shift
  group: string;              // Group name
  user_id?: string | null;     // Default user assigned to this
  day_of_week: number;          // Day of the week (5 = Mon-Fri, 0 = Saturday-Sunday)
  start_time: string;    // Start time (e.g., "10:00")
  end_time: string;      // End time (e.g., "18:00")
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// SupervisorShift Table
export interface SupervisorShift {
  id: string;                 // Unique ID for the shift
  group: string;              // G1 | G2 | G3
  user_id?: string | null;     // User assigned to this specific shift (nullable)
  shift_code?: string | null;  // Shift code (e.g. "SP Weekday", "FP Weekday", etc)
  start_date_time: DateTime;     // Start datetime
  end_date_time: DateTime;       // End datetime
  submitted: boolean;         // Is the shift complete?
  clocked_in: boolean;         // Has the user clocked in?
  clock_in_time?: DateTime;      // When did the user clock in?
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// SupervisorFixedShift Table
export interface SupervisorFixedShift {
  id: string;                 // Unique ID for the recurring shift
  group: string;              // G1 | G2 | G3
  shift_code?: string | null;  // Shift code (e.g. "SP Weekday", "FP Weekday", etc)
  user_id?: string | null;     // Default user assigned to this
  day_of_week: number;          // Day of the week (0 = Mon-Fri, 5 = Sat, 6 = Sun)
  start_time: string;    // Start time (e.g., "06:00")
  end_time: string;      // End time (e.g., "14:00")
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// RecurringShift Table
export interface RecurringShift {
  id: string;                 // Unique ID for the recurring shift
  model_id: string;            // Reference to the Model
  user_id?: string | null;     // Default user assigned to this recurring shift (nullable)
  day_of_week: number;          // Day of the week (0 = Mon-Fri, 5 = Sat, 6 = Sun)
  start_time: string;    // Start time (e.g., "10:00")
  end_time: string;      // End time (e.g., "18:00")
  shift_code: ShiftCodeKey;    // The shift code (e.g. "6-2 S", "2-10 FP", etc)
  created_at: DateTime;            // Timestamp of creation
  updated_at: DateTime;            // Timestamp of last update
}

// ExchangeRate Table (USD to GBP)
export interface ExchangeRate {
  id: string;                 // Unique ID for the exchange rate
  rate: number;               // Exchange rate from USD to GBP
  date: DateTime;                 // DateTime of the exchange rate
  created_at: DateTime;            // Timestamp of creation
  updated_at: DateTime;            // Timestamp of last update
}

/* ---------------------------------- PAY ------------------------------------- */
export type PayInvoiceStatus = 'generated' | 'submitted' | 'deferred'
export type PayInvoiceCategory = 'management' | 'sales' | 'scheduling' | 'referrals' | 'marketing' | 'tools' | 'commission' | 'other' | 'bonus' | 'ad/ded'

export interface PayWave {
  month_start: string // Date string
  locked_at?: string // timestamptz
}

export interface PayInvoice {
  id?: number
  user_id: string
  status: PayInvoiceStatus
  status_updated_at?: string // timestamptz
  invoice_number?: number
  first_generated_at?: string
  last_generated_at?: string
  period_month: string // Date string
  amount_gbp?: number
  created_at?: string
  updated_at?: string
}

export interface PayInvoiceLine {
  id?: number
  invoice_id: number
  category: PayInvoiceCategory
  amount_gbp: number
  breakdown?: any[] // jsonb
}
/* -------------------------------- END PAY ----------------------------------- */

// Pay Table
export interface Pay {
  id: string;
  user_id: string;
  month: number;
  year: number;
  shift_pay_result: {
    shift_pay: number;
    shift_weekday_pay: number;
    shift_weekend_pay: number;
    shift_overtime_pay: number;
    set_totals: {
      set: string;
      total_pay: number;
      regular_hours: number;
      weekend_ot_hours: number;
      weekday_ot_hours: number;
      regular_pay: number;
      weekend_ot_pay: number;
      weekday_ot_pay: number;
      total_regular_ppv: number;
      total_ot_ppv: number;
      nonppv: number;
    }[];
  };
  gg_swap_pay: GGSwapPayResult;
  schedule_pay: {
    total_schedule_pay: number;
    uk_posts_pay: {
      model_pays: { total_pay: number }[];
      total_pay: number;
    };
    fp_posts_pay: {
      model_pays: { total_pay: number }[];
      total_pay: number;
    };
    mass_dm_pay: {
      normal_mass_dm_pay: { total_pay: number; total_non_ot_pay: number; total_ot_pay: number }[];
      unlock_mass_dm_pay: { total_pay: number; total_non_ot_pay: number; total_ot_pay: number }[];
      total_pay: number;
      total_normal_pay: number;
      total_unlock_pay: number;
    };
    fp_mass_dm_pay: {
      unlock_mass_dm_pay: { total_pay: number; total_non_ot_pay: number; total_ot_pay: number }[];
      total_pay: number;
      total_unlock_pay: number;
    };
    spam_posts_pay: {
      total_pay: number;
    };
    set_totals: {
      set: string;
      total_pay: number;
    }[];
  },
  misc_pay: MiscPaymentsPayResult;
  hours_validation?: Record<string, {
    valid: boolean;
    regular_hours: number;
    weekday_ot_hours: number;
    weekend_ot_hours: number;
  }>;
  is_approximation: boolean;
  created_at: DateTime;
  updated_at: DateTime;

  // Absolute total
  total: number;
  // Totals by set
  totals: {
    set: string;
    total_pay: number;
    total_regular_pay: number;
    total_bonus_pay: number;
    total_shift_bonus_pay: number;
  }[];
}

// Invoice
export interface Invoice {
  id: string;
  file_name: string;
  generated_at: DateTime;
  generated_by: string;
  user_id: string;
  total_amount: number;
  invoice_number: number;
  submitted: boolean;
  submitted_at?: DateTime;
}

// Referral Table - stored in similar structure to pays (year/month)
export interface Referral {
  id: string;
  referrer_id: string;    // User ID of the person who made the referral
  referee_ids: string[];  // User IDs of the people who were referred
  set: number;           // Set number when the referral pay is given
  date: DateTime;         // Date of the referral
  created_at: DateTime;    // When the referral record was created
  updated_at: DateTime;    // When the referral record was last updated
  amount: number;        // Amount of the referral pay
  metadata?: {
    split_id?: string;
  };
}

// Holiday Table
export interface Holiday {
  id: string;
  user_id: string;
  start_date: DateTime;
  end_date: DateTime;
  status: 'approved' | 'pending';  // No rejected status as per requirements
  type: 'vacation' | 'sick' | 'personal';
  departments: ('Sales' | 'Mass DMs' | 'Posts' | 'GG Swap')[]; // Sales, Mass DMs, Posts
  scheduled_models?: string[];     // Model IDs for scheduling holidays
  notes?: string;
  approved_by?: string;
  approved_at?: DateTime;
  created_at: DateTime;
  updated_at: DateTime;
}
// Notification Types
export type NotificationType = 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';

// Notification Groups
export type NotificationGroup = 'URGENT' | 'EARNINGS' | 'TICKETS' | 'HOLIDAYS' | 'SHIFTS' | 'SYSTEM' | 'OTHER';

// Base Notification interface
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  group: NotificationGroup;
  created_at: DateTime;
  created_by: string;
  target_users?: string[];  // If specified, only these users will receive the notification
  link?: string;  // Optional link to navigate to when notification is clicked
  source?: string;  // Optional source of the notification
  source_user_id?: string;  // Optional user ID of the source
}

// User-specific notification status
export interface UserNotification {
  id: string;
  notification_id: string;
  user_id: string;
  read: boolean;
  read_at?: DateTime;
  created_at: DateTime;
}

export interface Role {
  id: string;
  name: string;
  permissions: Permission[];
  level: number;
  created_at: DateTime;
  updated_at: DateTime;
}

// OverwatchRequest Table
export interface OverwatchRequest {
  id: string;
  user_id: string;
  model_id: string;
  description: string;
  status: 'pending' | 'accepted' | 'declined';
  updated_by: string;
  updated_at: DateTime;
  created_at: DateTime;
}

// Add OvertimeRequest interface before the Permission type definitions
export interface OvertimeRequest {
  id: string;
  user_id: string;         // User making the request
  status: 'pending' | 'approved' | 'rejected';
  created_at: DateTime;
  updated_at: DateTime;
  notes?: string;         // Optional notes from the requester
  handled_by?: string;     // Admin who handled the request
  handled_at?: DateTime;       // When the request was handled
  is_partial_shift: boolean;
  requested_start_time: DateTime;  // For all requests
  requested_end_time: DateTime;    // For all requests
}

// Request type and priority enums
export type RequestType = 'bug' | 'feature';
export type RequestPriority = 'low' | 'medium' | 'high' | 'urgent';
export type RequestStatus = 'pending' | 'approved' | 'denied' | 'in_progress' | 'completed' | 'closed';

// Bug/Feature Request interface
export interface Request {
  id: string;
  user_id: string;           // User who created the request
  title: string;             // Title of the request
  description: string;        // Description of the request
  type: RequestType;         // Type: bug or feature
  priority: RequestPriority; // Priority level
  status: RequestStatus;     // Current status
  attachment_url?: string;   // Optional attachment URL (deprecated, use attachments)
  approved_by?: string;      // User who approved the request
  approved_at?: DateTime;     // When the request was approved
  denied_by?: string;        // User who denied the request
  denied_at?: DateTime;       // When the request was denied
  denied_reason?: string;    // Reason for denial
  created_at: DateTime;       // When the request was created
  updated_at: DateTime;       // When the request was last updated
  attachments?: RequestAttachment[]; // Array of attachments
  reporter_name?: string;    // Name of the user who reported
  reporter_discord?: string; // Discord username of the user who reported
}

// Request Attachment interface
export interface RequestAttachment {
  id: string;
  request_id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  storage_path: string;
  uploaded_by: string;
  created_at: DateTime;
}

// Ticket status enum
export type TicketStatus = 'open' | 'closed';

// Ticket priority enum
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

// Ticket type enum
export type TicketType = 'misc' | 'payment' | 'staffing' | 'complaints' | 'timeOff' | 'timeOffUrgent' | 'streamTimes' | 'accountAccess' | 'overwatch' | 'urgent' | 'scheduling';

// Ticket interface
export interface Ticket {
  id: string;                 // Unique ID for the ticket
  title: string;              // Title of the ticket
  description: string;        // Description of the ticket
  created_by: string;          // User ID of the creator
  status: TicketStatus;       // Status of the ticket
  priority: TicketPriority;   // Priority of the ticket
  type: TicketType;           // Type of the ticket
  participants: string[];     // Array of user IDs who are participants
  last_updated_at: DateTime;        // When the ticket was last updated
  created_at: DateTime;            // When the ticket was created
  closed_at?: DateTime;            // When the ticket was closed (if applicable)
  closed_by?: string;          // User ID of who closed the ticket (if applicable)
}

// Ticket message interface
export interface TicketMessage {
  id: string;                 // Unique ID for the message
  ticket_id: string;           // Reference to the ticket
  content: string;            // Content of the message
  created_by: string;          // User ID of the creator
  created_at: DateTime;         // When the message was created
  attachments?: TicketMessageAttachment[]; // Optional array of attachments
  is_system_message: boolean;   // Whether this is a system message
}

// Ticket message attachment interface
export interface TicketMessageAttachment {
  id: string;                 // Unique ID for the attachment
  message_id: string;          // Reference to the message
  url: string;                // URL of the attachment
  uploaded_by?: string;        // User ID of who uploaded the attachment
  created_at: DateTime;         // When the attachment was uploaded
}

// Ticket category manager interface
export interface TicketCategoryManager {
  id: string;                 // Unique ID for the category manager
  ticket_type: TicketType;     // Type of ticket this manager is responsible for
  pay_class: 'UK' | 'FP';      // Pay class this manager is responsible for
  manager_ids: string[];       // Array of user IDs who should be added to tickets of this type/pay_class
  role_ids: string[];         // Array of role IDs whose users should be added to tickets of this type/pay_class
  created_at: DateTime;            // When the category manager was created
  updated_at: DateTime;            // When the category manager was last updated
}

/* Schedule Interfaces */
export const SCHEDULE_SHIFT_TYPES = ['MASS_DM', 'FP_MASS_DM', 'UK_POSTS', 'FP_POSTS'] as const;
export type ScheduleShiftType = typeof SCHEDULE_SHIFT_TYPES[number];

export interface ScheduleShift {
  id: string;
  user_id: string;
  model_id: string;
  type: ScheduleShiftType;
  date: DateTime;
  is_ot: boolean;
  completed_at?: DateTime;
  created_at?: DateTime;
  updated_at?: DateTime;
}

export interface ScheduleFixedShift {
  id: string;
  type: ScheduleShiftType;
  model_id: string;
  user_id: string | null;
  created_at?: DateTime;
  updated_at?: DateTime;
}

export interface ScheduleShiftPayException {
  id: string;
  model_id?: string;
  user_id?: string;
  type: ScheduleShiftType;
  amount: number;
  created_at: DateTime;
  updated_at: DateTime;
}

export interface GGSwapShift {
  id: string;
  user_id: string;
  model_id: string;
  date: DateTime;
  is_cover: boolean;
  created_at?: DateTime;
  updated_at?: DateTime;
}

export interface GGSwapFixedShift {
  id: string;
  model_id: string;
  user_id: string | null;
  created_at?: DateTime;
  updated_at?: DateTime;
}

export interface GGSwapEntry {
  id: string;
  user_id: string;
  amount: number;
  month: DateTime;
  created_at: DateTime;
  updated_at: DateTime;
}

export interface GGModelAmountEntry {
  id: string;
  model_id: string;
  amount: number;
  month: DateTime;
  created_at: DateTime;
  updated_at: DateTime;
}

export interface ModelDaysEntry {
  model_id: string;
  days: number;
}

// ModelResetTimer Table
export interface ModelResetTimer {
  id: string;                 // Unique ID for the reset timer
  model_id: string;            // Reference to the Model
  time: DateTime;             // Time of the reset timer
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// ModelFormat Table
export interface ModelFormat {
  id: string;                 // Unique ID for the format
  model_id: string;            // Reference to the Model
  items: ModelFormatItem[];   // List of format items
  created_at: DateTime;         // Timestamp of creation
  updated_at: DateTime;         // Timestamp of last update
}

// Individual format item
export interface ModelFormatItem {
  id: string;                 // Unique ID for the format item
  time: string;               // Time in 24-hour format (e.g., "12:15")
  type: 'interaction' | 'promo' | 'text' | 'conditional';
  // For text type
  content?: string;           // Emoji or text content
  // For promo type
  group?: string;             // Group letter (A, B, etc.) for cross-group promo
  model?: string;             // Specific model name for cross-model promo
  model_id?: string;           // Actual model ID when promo is assigned to specific model
  launch_group?: number;       // Launch group number (independent of model groups)
  // For conditional type (different AM/PM content)
  am?: {
    type: 'interaction' | 'promo' | 'text';
    content?: string;
    group?: string;
    model?: string;
    model_id?: string;         // Model ID for AM promo
    launch_group?: number;     // Launch group for AM promo
  };
  pm?: {
    type: 'interaction' | 'promo' | 'text';
    content?: string;
    group?: string;
    model?: string;
    model_id?: string;         // Model ID for PM promo
    launch_group?: number;     // Launch group for PM promo
  };
}

// Scheduling Pay Result
export interface SchedulingPayResult {
  uk_posts_pay: number;
  fp_posts_pay: number;
  mass_dm_pay: number;
  fp_mass_dm_pay: number;
  spam_posts_pay: number;
  set_totals: Record<string, { total: number }>;
  total_schedule_pay: number;
  is_approximation: boolean;
}
/* End Schedule Interfaces */

/* GG Swap Interfaces */
export interface GGSwapPayResult {
  total: number;
  set_totals: {
    set: string;
    total_pay: number;
  }[];
}
/* End GG Swap Interfaces */

/* Misc Pay Interfaces */
export const MISC_PAYMENT_CATEGORIES = ['management', 'marketing', 'other', 'tools', 'ad/ded'] as const;
export type MiscPaymentCategory = typeof MISC_PAYMENT_CATEGORIES[number];

export const MISC_PAYMENT_SALES_TYPES = ['lead'] as const;
export type MiscPaymentSalesType = typeof MISC_PAYMENT_SALES_TYPES[number];

export const MISC_PAYMENT_CURRENCY_TYPES = ['gbp', 'usd'] as const;
export type MiscPaymentCurrencyType = typeof MISC_PAYMENT_CURRENCY_TYPES[number];

export interface MiscPayment {
  id: string;
  user_id: string;
  amount: number;
  bonus: number;
  category: MiscPaymentCategory;
  set: number;
  date: DateTime;
  created_at: DateTime;
  updated_at: DateTime;
  metadata?: {
    group?: string;
    sales_type?: MiscPaymentSalesType;
    hours_worked?: number;
    currency_type?: MiscPaymentCurrencyType;
    days_worked?: number;
    tg_premiums?: number;
    price_per_premium?: number;
    notes?: string;
    split_id?: string;
  };
}

export interface MiscPaymentsPayResult {
  total: number;
  management: {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
  marketing: {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
  other: {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
  tools: {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
  'ad/ded': {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
  bonus: {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
  referrals: {
    total: number;
    setTotals: Record<string, { total: number }>;
  };
}

/* End Misc Pay Interfaces */

/* Model List Interfaces */
export interface ModelList {
  id: string;
  scheduling_group: string;
  model_list: string[];  // Array of model IDs, randomized
  created_at: DateTime;
  updated_at: DateTime;
}

export interface ModelLists {
  id: string;
  lists: ModelList[];
  created_at: DateTime;
  updated_at: DateTime;
}

export interface DailyModelList {
  id: string;                                    // {model_id}_{date}
  model_id: string;
  date: DateTime;
  created_at: DateTime;
  updated_at: DateTime;
}

export interface DailyModelListSlot {
  id: string;                                    // UUID for the slot
  list_id: string;                               // References daily_model_lists.id
  position: number;                              // Order in the list
  time: string;                                  // Time from format (e.g., "01:36")
  slot_period: 'all' | 'am' | 'pm';              // When this slot applies
  type: 'interaction' | 'promo' | 'text';
  model_id?: string;                             // Assigned model ID (nullable for non-promo slots)
  old_model_id?: string;                         // Old model ID if available, used for launch slots
  group_id?: string;                             // Links AM/PM slots from same format item
  metadata?: any;                                // Additional metadata (e.g., promo group/model names)
  created_at: DateTime;
  updated_at: DateTime;
}

export interface HistoricalUsage {
  groups: {
    [promoting_group: string]: {
      [promoted_group: string]: { [model_id: string]: number }
    }
  };                                             // Group-based usage tracking
  updated_at: DateTime;
}

/* End Model List Interfaces */

/* Cost Interfaces */


export type CostCategory = 'Management' | 'Commission' | 'Tools' | 'Other' | 'Referral';

export interface Cost {
  id: string;
  name: string;
  set: number;
  amount: number;
  category: CostCategory;
  metadata?: {
    split_id?: string;
    notes?: string;
  };
  created_at: DateTime;
  updated_at: DateTime;
}

/* End Cost Interfaces */

/* Admin Action Interfaces */
export type AdminActionType =
  | 'CREATE_MODEL'
  | 'EDIT_MODEL'
  | 'DELETE_MODEL'
  | 'PAUSE_MODEL'
  | 'UNPAUSE_MODEL'
  | 'CREATE_SHIFT'
  | 'EDIT_SHIFT'
  | 'DELETE_SHIFT'
  | 'CREATE_ROLE'
  | 'EDIT_ROLE'
  | 'DELETE_ROLE'
  | 'CREATE_USER'
  | 'EDIT_USER'
  | 'DELETE_USER'
  | 'ADD_WARNING'
  | 'REMOVE_WARNING'
  | 'INVITE'
  | 'PROMOTE_TRIAL'
  | 'DEACTIVATE'
  | 'UPDATE_USER'
  | 'OTHER';

export const ADMIN_ACTION_TYPES: AdminActionType[] = [
  'CREATE_MODEL',
  'EDIT_MODEL',
  'DELETE_MODEL',
  'PAUSE_MODEL',
  'UNPAUSE_MODEL',
  'CREATE_SHIFT',
  'EDIT_SHIFT',
  'DELETE_SHIFT',
  'CREATE_ROLE',
  'EDIT_ROLE',
  'DELETE_ROLE',
  'CREATE_USER',
  'EDIT_USER',
  'DELETE_USER',
  'ADD_WARNING',
  'REMOVE_WARNING',
  'INVITE',
  'PROMOTE_TRIAL',
  'DEACTIVATE',
  'UPDATE_USER',
  'OTHER'
];

export interface AdminAction {
  type: AdminActionType;
  performed_by?: string | null;
  target_id: string;
  target_type: string;
  details: string;
  timestamp: DateTime;
  changes?: {
    before?: any;
    after?: any;
  };
  expires_at?: DateTime;
}
/* End Admin Action Interfaces */

/* Monthly Verification Interfaces */
export interface MonthlyVerification {
  id: string;
  year: number;
  month: number; // 0-11 (JavaScript months)
  verified_users: string[]; // Array of user IDs that have been selected for verification this month
  created_at: DateTime;
  updated_at: DateTime;
}
/* End Monthly Verification Interfaces */